import { useEffect, useRef, useState } from 'react'
import { Mic, MicOff, Pause, Play, X } from 'lucide-react'
import type { Bericht } from '../../../gedeeld/types'
import { kiesStem, Microfoon, naarTekst, spreek, spreekbaar, wekStem } from '../../lib/spraak'

/* ===========================================================================
 *  Praten met Cowork
 *
 *  Casper: "net als met chatgpt vragen stelt, en hij praat tegen je met
 *  antwoorden en of vragen."
 *
 *  Een rondje: luisteren -> omzetten (kantoor-pc) -> denken (de gewone vraag,
 *  met de vlag "gesproken") -> voorlezen -> weer luisteren. Alles komt ook
 *  gewoon in het gesprek te staan, met tabellen, kaarten en bestanden; die
 *  zie je zodra je dit scherm sluit.
 *
 *  Onderbreken: tik op de bol terwijl Cowork praat, dan stopt hij en luistert
 *  hij meteen. Twintig seconden niets gezegd: dan pauzeert hij, zodat er
 *  geen microfoon open blijft staan in een lege kamer.
 * =========================================================================== */

type Fase = 'opstarten' | 'luistert' | 'omzetten' | 'denkt' | 'praat' | 'pauze' | 'fout'

const UITLEG: Record<Fase, string> = {
  opstarten: 'Even de microfoon openen…',
  luistert: 'Ik luister…',
  omzetten: 'Even verstaan…',
  denkt: 'Ik zoek het op…',
  praat: 'Tik om te onderbreken of verder te gaan',
  pauze: 'Gepauzeerd. Tik om verder te praten.',
  fout: '',
}

export default function Praten({ vraag, onSluit }: {
  /** Een gesproken vraag stellen; geeft het antwoord als het af is (null bij een fout). */
  vraag: (tekst: string) => Promise<Bericht | null>
  onSluit: () => void
}) {
  const [fase, setFase] = useState<Fase>('opstarten')
  const [jij, setJij] = useState('')
  const [zin, setZin] = useState('')
  /* Het hele laatste antwoord: blijft staan als hij weer luistert. */
  const [antwoordTekst, setAntwoordTekst] = useState('')
  const [melding, setMelding] = useState<string | null>(null)
  const [geenStem, setGeenStem] = useState(false)
  const niveau = useRef(0)
  const bol = useRef<HTMLButtonElement>(null)
  const mic = useRef<Microfoon | null>(null)
  const stem = useRef<SpeechSynthesisVoice | null>(null)
  /* Breekt het lopende stuk af (luisteren of voorlezen). */
  const nu = useRef<AbortController | null>(null)
  const open = useRef(true)
  const draait = useRef(false)
  /* Altijd de nieuwste vraag-functie: het rondje loopt over vele renders heen. */
  const vraagNu = useRef(vraag)
  vraagNu.current = vraag

  /* De bol ademt mee met je stem: rechtstreeks op het element, niet via
     React, anders tekent het hele scherm twintig keer per seconde opnieuw. */
  useEffect(() => {
    let raf = 0
    const tik = () => {
      const s = 1 + Math.min(0.45, niveau.current * 6)
      bol.current?.style.setProperty('--schaal', String(s))
      niveau.current *= 0.85
      raf = requestAnimationFrame(tik)
    }
    raf = requestAnimationFrame(tik)
    return () => cancelAnimationFrame(raf)
  }, [])

  async function rondje() {
    if (draait.current || !open.current) return
    draait.current = true
    try {
      while (open.current) {
        const ab = new AbortController()
        nu.current = ab
        setFase('luistert')
        let opname: Blob | null
        try {
          /* Kan gooien: de microfoon werd geweigerd (en de bol is daarna
             opnieuw ingedrukt), of hij is losgetrokken. Zonder dit bleef het
             scherm op "Ik luister…" staan en luisterde er niets. */
          if (!mic.current) mic.current = new Microfoon()
          opname = await mic.current.luister(ab.signal, (n) => { niveau.current = Math.max(niveau.current, n) })
        } catch (e) {
          if (!open.current) return
          setMelding(e instanceof Error ? e.message : String(e))
          setFase('fout')
          return
        }
        if (!open.current) return
        if (!opname) { if (!ab.signal.aborted) setFase('pauze'); return }

        setFase('omzetten')
        let tekst = ''
        try {
          tekst = await naarTekst(opname)
        } catch (e) {
          setMelding(e instanceof Error ? e.message : String(e))
          setFase('fout')
          return
        }
        if (!open.current) return
        if (!tekst) { setMelding('Dat verstond ik niet. Zeg het nog eens?'); continue }
        setMelding(null)
        setJij(tekst)

        setFase('denkt')
        const antwoord = await vraagNu.current(tekst)
        if (!open.current) return
        if (!antwoord || antwoord.fout) {
          setMelding(antwoord?.tekst ?? 'Cowork kwam er niet uit. Probeer het nog eens.')
          setFase('fout')
          return
        }

        let zeg = spreekbaar(antwoord.tekst)
        if (antwoord.bestanden?.length) zeg += ' Het bestand staat in het gesprek.'
        /* De ondertitel van het vorige antwoord weg; na onderbreken bleef
           daar anders die ene afgebroken zin staan. */
        setZin('')
        setAntwoordTekst(zeg)
        setFase('praat')
        const pr = new AbortController()
        nu.current = pr
        if (stem.current && zeg) {
          await spreek(zeg, stem.current, pr.signal, setZin)
          setZin('')
        } else {
          /* Geen stem op dit apparaat: dan als tekst, met de tijd om te lezen
             (of tik op de bol om meteen verder te gaan). */
          await new Promise<void>((r) => {
            const t = setTimeout(r, Math.min(15_000, 2000 + zeg.length * 50))
            pr.signal.addEventListener('abort', () => { clearTimeout(t); r() }, { once: true })
          })
        }
      }
    } finally {
      draait.current = false
    }
  }

  /* Openen: microfoon, stem, en meteen luisteren. */
  useEffect(() => {
    open.current = true
    void (async () => {
      try {
        stem.current = await kiesStem()
        setGeenStem(!stem.current)
        mic.current = new Microfoon()
        await mic.current.open()
        void rondje()
      } catch (e) {
        setMelding(e instanceof Error ? e.message : String(e))
        setFase('fout')
      }
    })()
    return () => {
      open.current = false
      nu.current?.abort()
      speechSynthesis.cancel()
      mic.current?.sluit()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const toets = (e: KeyboardEvent) => { if (e.key === 'Escape') onSluit() }
    window.addEventListener('keydown', toets)
    return () => window.removeEventListener('keydown', toets)
  }, [onSluit])

  /** De bol: onderbreken, pauzeren of verder. */
  function opBol() {
    wekStem()
    if (fase === 'praat') { nu.current?.abort(); return }
    if (fase === 'luistert') { nu.current?.abort(); setFase('pauze'); return }
    if (fase === 'pauze' || fase === 'fout') { setMelding(null); void rondje() }
  }

  const label = fase === 'fout' ? (melding ?? 'Er ging iets mis.') : UITLEG[fase]

  return (
    <div className="praten" role="dialog" aria-modal="true" aria-label="Praten met Cowork">
      <div className="praten-kop">
        <span className="praten-titel">Praten met Cowork</span>
        <button type="button" className="praten-sluit" onClick={onSluit} aria-label="Stoppen met praten">
          <X size={20} />
        </button>
      </div>

      <div className="praten-midden">
        <button ref={bol} type="button" className={`praten-bol ${fase}`} onClick={opBol}
          aria-label={fase === 'praat' ? 'Onderbreken' : fase === 'luistert' ? 'Pauzeren' : 'Verder praten'}>
          <span className="praten-bol-kern">
            {fase === 'pauze' || fase === 'fout' ? <Play size={34} /> : fase === 'luistert' ? <Mic size={34} /> : fase === 'praat' ? <Pause size={30} /> : null}
          </span>
        </button>
        <p className={`praten-status ${fase === 'fout' ? 'fout' : ''}`} aria-live="polite">{label}</p>
        {melding && fase !== 'fout' && <p className="praten-melding" aria-live="polite">{melding}</p>}
      </div>

      <div className="praten-onder" aria-live="polite">
        {jij && <p className="praten-jij"><span>Jij</span>{jij}</p>}
        {(zin || antwoordTekst) && <p className="praten-cowork"><span>Cowork</span>{zin || antwoordTekst}</p>}
        {geenStem && (
          <p className="praten-noot">
            <MicOff size={13} aria-hidden /> Dit apparaat heeft geen Nederlandse stem; de antwoorden staan hier als tekst.
          </p>
        )}
        <p className="praten-noot">Wat je zegt wordt op de computer op kantoor omgezet naar tekst, en daarna gewist.</p>
      </div>
    </div>
  )
}
